/**
 * Announcement Priority Badge Component
 * 
 * Displays a colored badge indicating the priority level of an announcement
 */

import React from 'react';
import styled from 'styled-components';
import { AlertCircle, Info, AlertTriangle, Zap } from 'lucide-react';
import { AnnouncementPriority } from '../../@types/announcement';
import { ThemeColors, Spacing, Typography, BorderRadius } from '../../styles/ThemeStyles';

interface AnnouncementPriorityBadgeProps {
  priority: AnnouncementPriority;
  size?: 'sm' | 'md' | 'lg';
}

const BadgeContainer = styled.div<{ priority: AnnouncementPriority; size: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
  padding: 7px;
  border-radius: 5px;
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return Typography.fontSize.xs;
      case 'lg': return Typography.fontSize.sm;
      default: return Typography.fontSize.xs;
    }
  }};
  font-weight: ${Typography.fontWeight.semiBold};
  color: white;
  background-color: ${props => {
    switch (props.priority) {
      case 'CRITICAL': return '#DC2626'; // red-600
      case 'HIGH': return '#EA580C'; // orange-600
      case 'MEDIUM': return '#F59E0B'; // amber-500
      case 'LOW': return '#3B82F6'; // blue-500
      default: return ThemeColors.gray[500];
    }
  }};
`;

const IconWrapper = styled.span`
  display: flex;
  align-items: center;
`;

export const AnnouncementPriorityBadge: React.FC<AnnouncementPriorityBadgeProps> = ({
  priority,
  size = 'md'
}) => {
  const getIcon = () => {
    switch (priority) {
      case 'CRITICAL':
        return <Zap size={size === 'sm' ? 12 : size === 'lg' ? 16 : 14} />;
      case 'HIGH':
        return <AlertTriangle size={size === 'sm' ? 12 : size === 'lg' ? 16 : 14} />;
      case 'MEDIUM':
        return <AlertCircle size={size === 'sm' ? 12 : size === 'lg' ? 16 : 14} />;
      case 'LOW':
        return <Info size={size === 'sm' ? 12 : size === 'lg' ? 16 : 14} />;
      default:
        return <Info size={size === 'sm' ? 12 : size === 'lg' ? 16 : 14} />;
    }
  };

  return (
    <BadgeContainer priority={priority} size={size}>
      <IconWrapper>{getIcon()}</IconWrapper>
      <span>{priority}</span>
    </BadgeContainer>
  );
};

